import { Layout } from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Recycle, Package, Users, Factory, Scissors, Leaf } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/LanguageContext";

export default function WhatDoesItMean() {
  const { t } = useLanguage();

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is deadstock?",
        acceptedAnswer: { "@type": "Answer", text: "Deadstock is unused new textile material left over from production — cancelled orders, overproduction, minimum-order overruns or discontinued lines." }
      },
      {
        "@type": "Question",
        name: "What are sorted secondhand textiles?",
        acceptedAnswer: { "@type": "Answer", text: "Post-consumer garments and fabrics collected, cleaned and sorted by fibre, colour or category so they can be reused, remade or recycled at scale." }
      },
      {
        "@type": "Question",
        name: "What is remake in fashion?",
        acceptedAnswer: { "@type": "Answer", text: "Remake is the process of transforming existing garments or textiles into new products, extending their life and reducing waste." }
      }
    ]
  };

  return (
    <Layout>
      <Helmet>
        <title>{t("whatDoesItMean.title")} | HIDNstock</title>
        <meta name="description" content={t("whatDoesItMean.subtitle")} />
        <link rel="canonical" href="https://hidnstock.com/what-does-it-mean" />
        <meta property="og:title" content={`${t("whatDoesItMean.title")} | HIDNstock`} />
        <meta property="og:description" content={t("whatDoesItMean.subtitle")} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://hidnstock.com/what-does-it-mean" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${t("whatDoesItMean.title")} | HIDNstock`} />
        <meta name="twitter:description" content={t("whatDoesItMean.subtitle")} />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/50 to-background">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-6">{t("whatDoesItMean.badge")}</Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">{t("whatDoesItMean.title")}</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">{t("whatDoesItMean.subtitle")}</p>
          </div>
        </div>
      </section>

      {/* B2B */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-primary-foreground/20 rounded-xl flex items-center justify-center flex-shrink-0"><Users className="h-6 w-6" /></div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{t("whatDoesItMean.b2bTitle")}</h2>
            </div>
            <p className="text-lg text-primary-foreground/90 leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: t("whatDoesItMean.b2bDesc1") }} />
            <p className="text-lg text-primary-foreground/90 leading-relaxed mb-6">{t("whatDoesItMean.b2bDesc2")}</p>
            <ul className="space-y-3 text-primary-foreground/90 mb-6">
              <li className="flex items-start gap-3"><span className="font-bold">•</span><span dangerouslySetInnerHTML={{ __html: t("whatDoesItMean.b2bBullet1") }} /></li>
              <li className="flex items-start gap-3"><span className="font-bold">•</span><span dangerouslySetInnerHTML={{ __html: t("whatDoesItMean.b2bBullet2") }} /></li>
              <li className="flex items-start gap-3"><span className="font-bold">•</span><span dangerouslySetInnerHTML={{ __html: t("whatDoesItMean.b2bBullet3") }} /></li>
              <li className="flex items-start gap-3"><span className="font-bold">•</span><span dangerouslySetInnerHTML={{ __html: t("whatDoesItMean.b2bBullet4") }} /></li>
            </ul>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">{t("whatDoesItMean.b2bDesc3")}</p>
          </div>
        </div>
      </section>

      {/* Deadstock */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">{t("whatDoesItMean.deadstockTitle")}</h2>
            <Card className="p-8 border-0 shadow-soft">
              <CardContent className="p-0">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0"><Package className="h-6 w-6 text-primary" /></div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{t("whatDoesItMean.deadstockSubtitle")}</h3>
                    <Badge variant="secondary" className="mb-3">{t("whatDoesItMean.deadstockAlias")}</Badge>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: t("whatDoesItMean.deadstockDesc") }} />
                <ul className="space-y-2 text-muted-foreground mb-6">
                  <li>• {t("whatDoesItMean.deadstockReason1")}</li>
                  <li>• {t("whatDoesItMean.deadstockReason2")}</li>
                  <li>• {t("whatDoesItMean.deadstockReason3")}</li>
                  <li>• {t("whatDoesItMean.deadstockReason4")}</li>
                </ul>
                <Separator className="my-6" />
                <h4 className="font-semibold text-lg mb-4">{t("whatDoesItMean.complexityTitle")}</h4>
                <p className="text-muted-foreground leading-relaxed mb-4">{t("whatDoesItMean.complexityDesc1")}</p>
                <p className="text-muted-foreground leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: t("whatDoesItMean.complexityDesc2") }} />
                <Separator className="my-6" />
                <h4 className="font-semibold text-lg mb-4">{t("whatDoesItMean.transitionalTitle")}</h4>
                <p className="text-muted-foreground leading-relaxed mb-4">{t("whatDoesItMean.transitionalDesc1")}</p>
                <p className="text-muted-foreground leading-relaxed mb-4">{t("whatDoesItMean.transitionalDesc2")}</p>
                <ul className="space-y-2 text-muted-foreground mb-6">
                  <li dangerouslySetInnerHTML={{ __html: "• " + t("whatDoesItMean.transitionalBenefit1") }} />
                  <li dangerouslySetInnerHTML={{ __html: "• " + t("whatDoesItMean.transitionalBenefit2") }} />
                  <li dangerouslySetInnerHTML={{ __html: "• " + t("whatDoesItMean.transitionalBenefit3") }} />
                  <li dangerouslySetInnerHTML={{ __html: "• " + t("whatDoesItMean.transitionalBenefit4") }} />
                </ul>
                <div className="bg-muted/50 rounded-lg p-6">
                  <p className="text-muted-foreground leading-relaxed italic">{t("whatDoesItMean.transitionalQuote")}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Separator className="max-w-4xl mx-auto" />

      {/* Sorted Secondhand */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">{t("whatDoesItMean.sortedTitle")}</h2>
            <Card className="p-8 border-0 shadow-soft">
              <CardContent className="p-0">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-secondary/20 rounded-xl flex items-center justify-center flex-shrink-0"><Leaf className="h-6 w-6 text-secondary" /></div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{t("whatDoesItMean.sortedSubtitle")}</h3>
                    <Badge variant="secondary" className="mb-3">{t("whatDoesItMean.sortedBadge")}</Badge>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: t("whatDoesItMean.sortedDesc") }} />
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold mb-2">{t("whatDoesItMean.categorizedBy")}</h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• {t("whatDoesItMean.cat1")}</li>
                      <li>• {t("whatDoesItMean.cat2")}</li>
                      <li>• {t("whatDoesItMean.cat3")}</li>
                      <li>• {t("whatDoesItMean.cat4")}</li>
                      <li>• {t("whatDoesItMean.cat5")}</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">{t("whatDoesItMean.benefitsFor")}</h4>
                    <ul className="space-y-1 text-muted-foreground text-sm">
                      <li>• {t("whatDoesItMean.ben1")}</li>
                      <li>• {t("whatDoesItMean.ben2")}</li>
                      <li>• {t("whatDoesItMean.ben3")}</li>
                      <li>• {t("whatDoesItMean.ben4")}</li>
                      <li>• {t("whatDoesItMean.ben5")}</li>
                    </ul>
                  </div>
                </div>
                <p className="text-muted-foreground leading-relaxed">{t("whatDoesItMean.sortedSummary")}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Separator className="max-w-4xl mx-auto" />

      {/* Remake, Redesign, Upcycling */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">{t("whatDoesItMean.remakeTitle")}</h2>
            <div className="grid gap-8">
              <Card className="p-8 border-0 shadow-soft">
                <CardContent className="p-0">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-secondary/20 rounded-xl flex items-center justify-center flex-shrink-0"><Scissors className="h-6 w-6 text-secondary" /></div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{t("whatDoesItMean.whatIsRemake")}</h3>
                      <Badge className="mb-3 bg-secondary/20 text-secondary-foreground border-secondary/40">{t("whatDoesItMean.remakeBadge")}</Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: t("whatDoesItMean.remakeDesc") }} />
                  <ul className="space-y-2 text-muted-foreground mb-4">
                    <li dangerouslySetInnerHTML={{ __html: "• " + t("whatDoesItMean.remakePoint1") }} />
                    <li dangerouslySetInnerHTML={{ __html: "• " + t("whatDoesItMean.remakePoint2") }} />
                    <li dangerouslySetInnerHTML={{ __html: "• " + t("whatDoesItMean.remakePoint3") }} />
                    <li dangerouslySetInnerHTML={{ __html: "• " + t("whatDoesItMean.remakePoint4") }} />
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">{t("whatDoesItMean.remakeSummary")}</p>
                </CardContent>
              </Card>

              <Card className="p-8 border-0 shadow-soft">
                <CardContent className="p-0">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-secondary/20 rounded-xl flex items-center justify-center flex-shrink-0"><Factory className="h-6 w-6 text-secondary" /></div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{t("whatDoesItMean.whatIsRedesign")}</h3>
                      <Badge className="mb-3 bg-secondary/20 text-secondary-foreground border-secondary/40">{t("whatDoesItMean.redesignBadge")}</Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: t("whatDoesItMean.redesignDesc") }} />
                  <ul className="space-y-2 text-muted-foreground mb-4">
                    <li>• {t("whatDoesItMean.redesignPoint1")}</li>
                    <li>• {t("whatDoesItMean.redesignPoint2")}</li>
                    <li>• {t("whatDoesItMean.redesignPoint3")}</li>
                    <li>• {t("whatDoesItMean.redesignPoint4")}</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">{t("whatDoesItMean.redesignSummary")}</p>
                </CardContent>
              </Card>

              <Card className="p-8 border-0 shadow-soft">
                <CardContent className="p-0">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center flex-shrink-0"><Recycle className="h-6 w-6 text-accent-foreground" /></div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{t("whatDoesItMean.whatIsUpcycling")}</h3>
                      <Badge className="mb-3 bg-accent/20 text-accent-foreground border-accent/40">{t("whatDoesItMean.upcyclingBadge")}</Badge>
                    </div>
                  </div>
                  <p className="text-muted-foreground leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: t("whatDoesItMean.upcyclingDesc") }} />
                  <ul className="space-y-2 text-muted-foreground mb-4">
                    <li>• {t("whatDoesItMean.upcyclingPoint1")}</li>
                    <li>• {t("whatDoesItMean.upcyclingPoint2")}</li>
                    <li>• {t("whatDoesItMean.upcyclingPoint3")}</li>
                    <li>• {t("whatDoesItMean.upcyclingPoint4")}</li>
                  </ul>
                  <p className="text-muted-foreground leading-relaxed">{t("whatDoesItMean.upcyclingSummary")}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Summary CTA */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">{t("whatDoesItMean.ctaTitle")}</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">{t("whatDoesItMean.ctaDesc")}</p>
            <p className="text-muted-foreground">{t("whatDoesItMean.ctaTagline")}</p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
